import { useEffect, useRef, useState } from "react";
import { Send, Bot, User as UserIcon, Loader2, Image as ImageIcon, X, Paperclip, FileText } from "lucide-react";
import { api, formatDetail } from "../lib/api";

export default function ChatPanel({
    session,
    onUpdate,
    placeholder = "Escribe tu mensaje…",
    emptyState,
    enableInfographic = false,
    enableFileUpload = false,
}) {
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const [showInfo, setShowInfo] = useState(false);
    const [infoPrompt, setInfoPrompt] = useState("");
    const [generatingImg, setGeneratingImg] = useState(false);
    const [pendingFile, setPendingFile] = useState(null); // {id, name}
    const [uploadingFile, setUploadingFile] = useState(false);
    const fileInputRef = useRef(null);
    const endRef = useRef(null);
    const messages = session?.messages || [];

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.length]);

    async function send() {
        if ((!text.trim() && !pendingFile) || !session) return;
        setSending(true);
        setError("");
        try {
            const payload = {
                session_id: session.id,
                text: text.trim() || `He adjuntado el archivo "${pendingFile?.name}". Ayúdame a analizarlo.`,
            };
            if (pendingFile) payload.file_id = pendingFile.id;
            const { data } = await api.post("/ai/messages", payload);
            setText("");
            setPendingFile(null);
            onUpdate?.({ ...session, messages: data.messages });
        } catch (e) {
            setError(formatDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSending(false);
        }
    }

    async function handleFileSelect(e) {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploadingFile(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/files/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPendingFile({ id: data.id, name: data.original_filename || f.name });
        } catch (e2) {
            setError(formatDetail(e2.response?.data?.detail) || e2.message);
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }

    return (
        <div className="flex flex-col h-full" data-testid="chat-panel">
            <div className="flex-1 overflow-y-auto edu-scroll p-6 space-y-4">
                {messages.length === 0 && emptyState}
                {messages.map((m, i) => {
                    const isUser = m.role === "user";
                    return (
                        <div key={i} className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
                            <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    isUser ? "bg-edu-navy text-white" : "bg-[#FFF4D9] text-edu-navy"
                                }`}
                            >
                                {isUser ? <UserIcon size={14} /> : <Bot size={14} />}
                            </div>
                            <div
                                className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                                    isUser
                                        ? "bg-edu-navy text-white rounded-tr-sm"
                                        : "bg-white border border-edu rounded-tl-sm text-[#0A0A0A]"
                                }`}
                            >
                                {m.attachment && (
                                    <div className={`text-xs mb-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg ${isUser ? "bg-white/20" : "bg-edu-soft"}`}>
                                        <FileText size={12} /> {m.attachment.filename}
                                    </div>
                                )}
                                {m.type === "image" && m.image_base64 ? (
                                    <img
                                        src={`data:image/png;base64,${m.image_base64}`}
                                        alt="generated"
                                        className="rounded-lg max-w-full"
                                    />
                                ) : (
                                    m.content
                                )}
                            </div>
                        </div>
                    );
                })}
                {sending && (
                    <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#FFF4D9] flex items-center justify-center">
                            <Bot size={14} className="text-edu-navy" />
                        </div>
                        <div className="bg-white border border-edu rounded-2xl px-4 py-3 text-sm text-edu-muted flex items-center gap-2">
                            <Loader2 size={14} className="animate-spin" /> Pensando…
                        </div>
                    </div>
                )}
                <div ref={endRef} />
            </div>
            {error && <div className="px-6 pb-2 text-sm text-red-600">{error}</div>}
            <div className="border-t border-edu bg-white p-4">
                {pendingFile && (
                    <div className="mb-3 p-3 bg-edu-soft border border-edu rounded-xl flex items-center justify-between">
                        <span className="text-sm inline-flex items-center gap-2">
                            <FileText size={14} className="text-edu-navy" />
                            <span className="font-medium">{pendingFile.name}</span>
                            <span className="text-xs text-edu-muted">listo para enviar</span>
                        </span>
                        <button
                            onClick={() => setPendingFile(null)}
                            className="text-edu-muted hover:text-red-600"
                            data-testid="remove-pending-file"
                            aria-label="Quitar archivo"
                        >
                            <X size={14} />
                        </button>
                    </div>
                )}
                {showInfo && (
                    <div className="mb-3 p-3 bg-[#FFF4D9] border border-[#F5A623]/30 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold inline-flex items-center gap-2 text-edu-navy">
                                <ImageIcon size={14} /> Generar Infografía con IA
                            </span>
                            <button
                                onClick={() => setShowInfo(false)}
                                className="text-edu-muted hover:text-edu-navy"
                                data-testid="info-close"
                            >
                                <X size={14} />
                            </button>
                        </div>
                        <div className="flex gap-2">
                            <input
                                value={infoPrompt}
                                onChange={(e) => setInfoPrompt(e.target.value)}
                                placeholder="Ej. Las fases de la luna explicadas con dibujos…"
                                className="edu-input flex-1 text-sm py-2"
                                data-testid="info-prompt"
                            />
                            <button
                                onClick={generateInfographic}
                                disabled={!infoPrompt.trim() || generatingImg}
                                className="edu-btn-accent px-3 py-2 text-xs disabled:opacity-50 inline-flex items-center gap-1"
                                data-testid="info-generate"
                            >
                                {generatingImg ? <Loader2 size={12} className="animate-spin" /> : <ImageIcon size={12} />}
                                {generatingImg ? "Generando…" : "Generar"}
                            </button>
                        </div>
                    </div>
                )}
                <div className="flex items-end gap-2">
                    {enableInfographic && (
                        <button
                            onClick={() => setShowInfo((v) => !v)}
                            className="px-3 py-2.5 rounded-xl border border-edu hover:bg-gray-50 text-edu-navy"
                            title="Generar infografía"
                            data-testid="chat-image-button"
                            type="button"
                        >
                            <ImageIcon size={16} />
                        </button>
                    )}
                    {enableFileUpload && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".pdf,.doc,.docx,.txt,.md,.png,.jpg,.jpeg,.webp"
                                onChange={handleFileSelect}
                                className="hidden"
                                data-testid="chat-file-input"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="px-3 py-2.5 rounded-xl border border-edu hover:bg-gray-50 text-edu-navy"
                                title="Adjuntar archivo (PDF, Word, imagen)"
                                data-testid="chat-file-button"
                                type="button"
                                disabled={uploadingFile}
                            >
                                {uploadingFile ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
                            </button>
                        </>
                    )}
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder={placeholder}
                        rows={1}
                        data-testid="chat-input"
                        className="flex-1 resize-none edu-input min-h-[44px] max-h-32"
                    />
                    <button
                        onClick={send}
                        disabled={sending || !text.trim()}
                        data-testid="chat-send-button"
                        className="edu-btn-primary px-4 py-2.5 disabled:opacity-50 flex items-center gap-2"
                    >
                        <Send size={16} />
                    </button>
                </div>
            </div>
        </div>
    );

    async function generateInfographic() {
        if (!infoPrompt.trim() || !session) return;
        setGeneratingImg(true);
        setError("");
        try {
            const { data } = await api.post("/ai/image", {
                session_id: session.id,
                prompt: infoPrompt.trim(),
            });
            const newMsg = {
                role: "assistant",
                type: "image",
                content: infoPrompt.trim(),
                image_base64: data.image_base64,
                ts: new Date().toISOString(),
            };
            onUpdate?.({ ...session, messages: [...messages, newMsg] });
            setInfoPrompt("");
            setShowInfo(false);
        } catch (e) {
            setError(formatDetail(e.response?.data?.detail) || e.message);
        } finally {
            setGeneratingImg(false);
        }
    }
}
